/**
 * A PÁRBESZÉD FÓKUSZA — nyitás, csapda, visszaadás (#16/2).
 *
 * MIÉRT HOROG, ÉS MIÉRT EGY. Az `aria-modal="true"` egy ÍGÉRET: azt mondja a
 * képernyőolvasónak, hogy a párbeszéden kívül most nincs semmi. A böngésző
 * ezt NEM tartatja be — a `Tab` simán kiviszi a fókuszt a párbeszéd mögé, és
 * onnantól a felolvasó olyan gombokat mond be, amiket a képernyőn egy sötét
 * réteg takar. Az ígéretet a kódnak kell megtartania, és ha minden párbeszéd
 * maga tartja meg, akkor pont abból marad ki, amit senki nem próbál végig
 * billentyűvel. Ezért egy horog, négy hívóval.
 *
 * HÁROM DOLGOT CSINÁL, ÉS MIND A HÁROM KÜLÖN HIÁNY VOLT:
 *   • NYITÁSKOR BEVISZI a fókuszt — enélkül a képernyőolvasó meg sem tudja,
 *     hogy megnyílt valami, és a következő leütés a párbeszéd MÖGÉ megy
 *   • TARTJA: a `Tab` az utolsó elemről az elsőre fordul vissza
 *   • ZÁRÁSKOR VISSZAADJA oda, ahonnan jött — különben a billentyűzetes
 *     kolléga a lap elejéről kezdheti újra
 *
 * ⚠️ AZ `Escape` NEM ITT ÉL. A hívók regiszteréből jön (`useShortcuts`), vagy
 * a párbeszéd saját `onKeyDown`-jából — ez a horog a FÓKUSZRÓL szól. Kettős
 * bejegyzés két rétegen egy leütésre kettőt zárna.
 */

import { type MutableRefObject, type RefObject, useEffect, useRef } from 'react';

/**
 * AMI FÓKUSZÁLHATÓ — a `Tab`-sorrend, nem a teljes DOM.
 *
 * A `[tabindex="-1"]` SZÁNDÉKOSAN KIMARAD: a `overlay__backdrop` gomb és az
 * „Eredeti" keret is ilyen — egérrel elérhető, a `Tab`-nak nem célpont. Ha
 * ide sorolnánk, a csapda a háttérgombra fordítaná vissza a fókuszt, és a
 * könyvelő a párbeszéd bezárásán állna, minden kör végén.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * A NYITOTT PÁRBESZÉDEK VERME — mindig a LEGFELSŐ tartja a fókuszt.
 *
 * Kettő EGYSZERRE is állhat: a Beállítások lapon a megerősítő ablak fölött a
 * `Ctrl+K` kinyitja a palettát. Verem nélkül MINDKÉT csapda kezelné ugyanazt
 * a `Tab`-ot, és a fókusz oda-vissza ugrálna a két doboz közt.
 */
const openDialogs: HTMLElement[] = [];

function focusablesIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => {
    // A REJTETT ELEM NEM CÉLPONT. A böngésző kihagyja a `Tab`-sorból; a
    // csapda enélkül egy láthatatlan elemre tenné a fókuszt, és a kurzor
    // eltűnne a képernyőről.
    //
    // ⚠️ NEM `offsetParent`/`getClientRects` ALAPJÁN. Azok jsdomban MINDIG
    // üresek — a szűrő ott az ÖSSZES elemet kidobta volna, a teszt pedig a
    // tartalék ágat mérte volna csapda helyett. Ami mérhető, az a `hidden`
    // és a kiszámolt stílus; a valódi rejtés a felületen úgyis így történik.
    if (element.closest('[hidden]') !== null) {
      return false;
    }
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

/** A doboz csak `tabindex`-szel fogad fókuszt — enélkül a `focus()` néma. */
function makeFocusable(dialog: HTMLElement): void {
  if (!dialog.hasAttribute('tabindex')) {
    dialog.setAttribute('tabindex', '-1');
  }
}

/**
 * HOVA KELL A `Tab`-OT ELTÉRÍTENI — `null`, ha a böngésző jól lépteti.
 *
 * ⚠️ A KÜLSŐ FÓKUSZT IS VISSZAHOZZA. Nem csak a két végén fordul: ha a fókusz
 * valahogy mégis kikerült (kattintás a háttérre, vagy a doboz maga volt a
 * kezdőpont), a következő `Tab` a párbeszédbe lép VISSZA, nem tovább kifelé.
 */
function trapTarget(dialog: HTMLElement, shiftKey: boolean): HTMLElement | null {
  const focusables = focusablesIn(dialog);
  if (focusables.length === 0) {
    // NINCS HOVA LÉPNI — de kilépni sem szabad: a `Tab` a dobozon tartja a
    // fókuszt.
    //
    // ⚠️ A `tabindex` ITT IS FELKERÜL, nem csak nyitáskor. A párbeszéd
    // fókuszálható tartalommal is nyílhat, ami KÉSŐBB fogy el (minden gomb
    // `disabled` lesz); a nyitáskori ág olyankor nem futott le, és a
    // `dialog.focus()` attribútum híján NEM CSINÁLNA SEMMIT — a leütés némán
    // elnyelődne. *(Code review fogta meg, 09-18.)*
    makeFocusable(dialog);
    return dialog;
  }
  const firstStop = focusables[0];
  const lastStop = focusables[focusables.length - 1];
  const active = document.activeElement;

  if (!dialog.contains(active)) {
    return shiftKey ? lastStop : firstStop;
  }
  if (shiftKey && active === firstStop) {
    return lastStop;
  }
  if (!shiftKey && active === lastStop) {
    return firstStop;
  }
  return null;
}

/**
 * @param initial Ha adott, NYITÁSKOR ez kapja a fókuszt az első fókuszálható
 * elem helyett. A megerősítő ablaknak kell: ott a „Mégse" a kezdőpont, nem az
 * „Igen" — egy elütött `Enter` így nem inaktivál senkit.
 * @returns A párbeszéd DOBOZÁRA teendő ref. Nem az átlátszó rétegre: a
 * csapdának a kártya a határa, a réteg a háttérgombot is tartalmazza.
 *
 * ⚠️ `MutableRefObject`, NEM `RefObject`. A React 18 típusaiban a
 * `RefObject<T | null>` nem adható át `ref`-ként — a `LegacyRef` a
 * `RefObject<T>`-t várja, és a `null` nem fér bele. A `tsc --noEmit` fogta
 * meg; a suite zölden ment mellette (`CLAUDE.md`).
 */
export function useDialogFocus<T extends HTMLElement = HTMLDivElement>(
  initial?: RefObject<HTMLElement | null>
): MutableRefObject<T | null> {
  const container = useRef<T | null>(null);
  // REF, NEM FÜGGŐSÉG: a kezdő fókusz a NYITÁS pillanatának kérdése. A
  // függőséglistában egy újrarenderelt `initial` mindenkit visszarántana a
  // kezdőpontra, félig kitöltött űrlapon is.
  //
  // ⚠️ ÉS NEM ÍRJUK ÚJRA RENDERBEN. A `useRef(initial)` KEZDŐÉRTÉKE épp a
  // nyitás pillanatát őrzi, a mount-effekt pedig csak azt olvassa — egy
  // renderfázisú ref-írás ehhez semmit nem tesz hozzá, viszont a React nem
  // garantálja (egy eldobott render is mutálna). *(Code review, 09-18.)*
  const initialRef = useRef(initial);

  useEffect(() => {
    const dialog = container.current;
    if (dialog === null) {
      return;
    }

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    openDialogs.push(dialog);

    // A DOBOZ MAGA A TARTALÉK. Egy gomb nélküli párbeszédnél (töltődő űrlap)
    // különben a fókusz a `<body>`-n maradna, és a `Tab` a lap tetejéről
    // indulna — a párbeszéd mögül.
    const first = initialRef.current?.current ?? focusablesIn(dialog)[0] ?? dialog;
    if (first === dialog) {
      makeFocusable(dialog);
    }
    first.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') {
        return;
      }
      // CSAK A LEGFELSŐ PÁRBESZÉD CSAPDÁZ — lásd az `openDialogs` fejlécét.
      if (openDialogs[openDialogs.length - 1] !== dialog) {
        return;
      }
      const target = trapTarget(dialog, event.shiftKey);
      if (target === null) {
        // A PÁRBESZÉDEN BELÜL VAGYUNK, ÉS VAN MÉG HOVA LÉPNI — a `Tab` a
        // böngészőé. A csapda csak a KIJÁRATOKAT zárja, a bejárást nem
        // veszi át.
        return;
      }
      event.preventDefault();
      target.focus();
    }

    // A `window`-on, LEFUTÁS ELŐTT (`capture`). A dobozra kötve a már
    // kiszökött fókuszt nem érnénk el — pont azt az esetet nem, ami ellen a
    // csapda van.
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      // ⚠️ A SORREND SZÁMÍT: előbb megkérdezzük, legfelső voltunk-e, és csak
      // utána vesszük ki magunkat a veremből.
      const wasTop = openDialogs[openDialogs.length - 1] === dialog;
      const at = openDialogs.indexOf(dialog);
      if (at !== -1) {
        openDialogs.splice(at, 1);
      }
      // CSAK A LEGFELSŐ ADJA VISSZA A FÓKUSZT, és csak ha a nyitó még létezik.
      //
      // Az ALSÓ párbeszéd záródhat előbb — a Beállítások megerősítő ablaka
      // alól kifutó `Ctrl+K` palettánál —, és akkor a fókuszt a saját
      // nyitójára rántaná vissza: egy elemre, amit épp KÉT réteg takar. A
      // `Tab` már eddig is csak a legfelsőt illette meg; a visszaadás volt az
      // egyetlen pont, ahol ez az elv nem volt végigvive. Egy közben eltűnt
      // sorra fókuszálva pedig a böngésző a lap tetejére ugrana.
      // *(Code review fogta meg, méréssel, 09-18.)*
      if (wasTop && opener?.isConnected) {
        opener.focus();
      }
    };
  }, []);

  return container;
}
